import {FC} from 'react';
import {MenuSidebar} from '@components/MenuSidebar';
import {Button} from '@components/Button';
import {Icon} from '@components/Icon';
import {Nav} from '@components/Nav';
import {FormattedMessage} from 'react-intl';
import {Sidebar} from '@components/Sidebar';
import {VisuallyHidden} from '@components/VisuallyHidden';
import {Stack} from '@components/Stack';
import {ButtonGroup} from '@components/ButtonGroup';
import {ButtonToolbar} from '@components/ButtonToolbar';
import {Divider} from '@components/Divider';
import {Typography} from '@components/Typography';
import {getLocalizedRoute, isRoute, routes} from '@utils/routes';
import Link from 'next/link';
import {useLocale} from '@utils/locale';
import {useRouter} from 'next/router';
import {LanguageSwitcher} from './LanguageSwitcher';
import {LocaleEnum} from 'src/types/general';

export type SidebarMenuProps = {
    show: boolean;
    onHide: () => void;
};

export const SidebarMenu: FC<SidebarMenuProps> = ({show, onHide}) => {
    const locale = useLocale();
    const router = useRouter();

    return (
        <Sidebar specialVariant="menu" show={show} onHide={onHide}>
            <MenuSidebar>
                <Stack direction="horizontal" className="mb-4 mb-md-12 d-none d-md-flex justify-content-end">
                    <Button variant="icon" size="sm" onClick={onHide}>
                        <VisuallyHidden>Zavřít</VisuallyHidden>
                        <Icon.Close position="middle" />
                    </Button>
                </Stack>

                <ButtonGroup className="mb-10 mb-md-12 d-md-none">
                    <Link href={getLocalizedRoute(routes.serviceCentreIntroOwner, locale)} passHref>
                        <Button variant="secondary" size="sm">
                            <Typography variant="cta">
                                <FormattedMessage defaultMessage="Nabídnout nemovitost" />
                            </Typography>
                            <Icon.Key position="end" />
                        </Button>
                    </Link>
                </ButtonGroup>

                <Nav as="ul" className="flex-column d-xl-none">
                    <Nav.Item as="li" onClick={onHide}>
                        <Link href={getLocalizedRoute(routes.list, locale, {list: ''})} passHref>
                            <Nav.Link active={isRoute(router.asPath, routes.list, locale)}>
                                <FormattedMessage defaultMessage="Nemovitosti" />
                            </Nav.Link>
                        </Link>
                    </Nav.Item>
                    <Nav.Item as="li" onClick={onHide}>
                        <Link href={getLocalizedRoute(routes.serviceCentreIntroCustomer, locale)} passHref>
                            <Nav.Link href="#">
                                <FormattedMessage defaultMessage="Tipy pro zájemce" />
                            </Nav.Link>
                        </Link>
                    </Nav.Item>
                    <Nav.Item as="li" onClick={onHide}>
                        <Link href={getLocalizedRoute(routes.serviceCentrePremium, locale)} passHref>
                            <Nav.Link active={isRoute(router.asPath, routes.serviceCentrePremium, locale)}>
                                <FormattedMessage defaultMessage="Premium profil" />
                            </Nav.Link>
                        </Link>
                    </Nav.Item>
                    <Nav.Item as="li" onClick={onHide}>
                        <Link href={getLocalizedRoute(routes.serviceCentreSale, locale)} passHref>
                            <Nav.Link active={isRoute(router.asPath, routes.serviceCentreSale, locale)}>
                                <FormattedMessage defaultMessage="Služby" />
                            </Nav.Link>
                        </Link>
                    </Nav.Item>
                    <Nav.Item as="li" onClick={onHide}>
                        <Link href={getLocalizedRoute(routes.priceList, locale)} passHref>
                            <Nav.Link active={isRoute(router.asPath, routes.priceList, locale)}>
                                <FormattedMessage defaultMessage="Ceník inzerce" />
                            </Nav.Link>
                        </Link>
                    </Nav.Item>
                </Nav>

                <Divider className="my-10 my-md-12 d-xl-none" />

                <Nav as="ul" className="flex-column">
                    <Nav.Item as="li" onClick={onHide}>
                        <Link href={getLocalizedRoute(routes.aboutUs, locale)} passHref>
                            <Nav.Link href="#">
                                <FormattedMessage defaultMessage="O nás" />
                            </Nav.Link>
                        </Link>
                    </Nav.Item>
                    <Nav.Item as="li" onClick={onHide}>
                        <Nav.Link href={locale === LocaleEnum.sk ? 'https://www.bezrealitky.sk/blog/' : 'https://blog.bezrealitky.cz/'}>
                            <FormattedMessage defaultMessage="Blog Bezrealitky" />
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <MenuSidebar.Footer>
                    <ButtonToolbar className="mt-10 mt-md-12">
                        <ButtonGroup>
                            <LanguageSwitcher variant="button" />
                        </ButtonGroup>
                    </ButtonToolbar>
                </MenuSidebar.Footer>
            </MenuSidebar>
        </Sidebar>
    );
};
